// notificationText.js
//
// Safe rendering for the notification `description` field. The backend emits the
// description as RAW HTML with the actor's display name interpolated inside <b>
// tags, e.g. "<b>Gordon Maniskas</b> has cloned your <b>Wheel</b>"
// (NEMESIS-nemz17 / EW-1143). The actor name is attacker-controllable (a user can
// set their display name to `<img src=x onerror=...>` and trigger a
// notification-generating action against another user), so this field MUST be
// treated as untrusted and rendered as SAFE TEXT — never via innerHTML /
// dangerouslySetInnerHTML.
//
// Two exports:
//   inertHtmlToText(html)      -> plain string, tags stripped, entities decoded,
//                                 using an INERT parse (no script exec, no
//                                 subresource/`onerror` load — unlike
//                                 utils/helpers.js `htmlToText`, which assigns
//                                 live `.innerHTML`). Use for aria-label / title.
//   renderSafeDescription(html)-> array of React text/`<span>` nodes that PRESERVE
//                                 the intended <b> bolding while routing ALL text
//                                 through React's auto-escaping text nodes. Any
//                                 non-<b> markup (an injected <img>/<script>/etc.)
//                                 is flattened to inert text, so nothing an
//                                 attacker injects can execute or even render as
//                                 live markup.
import React from "react";

const hasDocument =
  typeof document !== "undefined" &&
  typeof document.createElement === "function";

// Inert HTML -> text. Parses via a <template> element, whose contents live in a
// separate, inert "template contents owner" document that is never rendered: per
// the HTML spec scripts do not run and subresources (img src, and therefore
// onerror) are NOT fetched. This is the safe counterpart to the innerHTML-on-a-
// live-div helper in utils/helpers.js (a detached div can still trigger image
// loads in some engines; template content cannot), and — unlike a full-document
// DOMParser parse — it preserves leading/trailing whitespace (head-insertion
// modes would otherwise swallow leading spaces).
export function inertHtmlToText(html) {
  if (html == null) return "";
  const str = String(html);
  if (str === "") return "";
  if (hasDocument) {
    try {
      const tpl = document.createElement("template");
      tpl.innerHTML = str;
      return (tpl.content && tpl.content.textContent) || "";
    } catch (e) {
      // fall through to the regex fallback
    }
  }
  // SSR / no-document fallback: strip tags, then decode the handful of named
  // entities the backend actually emits. Never assigns innerHTML to a live node.
  return str
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

// Split a raw description into <b>…</b> segments (bold) and everything else
// (plain), then flatten each segment to inert text. Returns React nodes; the
// caller renders them directly (React escapes every string child, so nothing
// here can ever become live HTML).
export function renderSafeDescription(html) {
  if (html == null) return [];
  const str = String(html);
  if (str === "") return [];
  const parts = str.split(/(<b>[\s\S]*?<\/b>)/gi).filter(Boolean);
  return parts.map((part, index) => {
    const boldMatch = /^<b>([\s\S]*?)<\/b>$/i.exec(part);
    if (boldMatch) {
      return React.createElement(
        "span",
        { key: index, style: { fontWeight: "bold" } },
        inertHtmlToText(boldMatch[1])
      );
    }
    return React.createElement(
      "span",
      { key: index },
      inertHtmlToText(part)
    );
  });
}

// Build the notification sentence from the BE #1434 structured fields instead of
// the untrusted HTML `description`. `actorName` is the PLAIN-TEXT actor_name
// (correct apostrophes, no tags, JSON-escaped — never HTML). `description` is the
// post-XSS-fix plain-text sentence "{actor_name} {action…}" whose interpolated
// user values are format_html-ESCAPED (so a raw apostrophe arrives as `&#x27;`),
// which we DECODE + flatten to inert text via inertHtmlToText.
//
// Returns React nodes: the actor name in a <strong> (bold, default colour) plus
// the remaining action phrase in a muted <span>. The bold is applied ONLY when
// the decoded description actually starts with the actor name (the reliable BE
// shape) — otherwise we fall back to the clean plain text so a name is never
// duplicated or mis-bolded. With no actor (system notification) the whole
// (decoded) text renders as muted plain text. Nothing here ever uses innerHTML,
// so a hostile display name is inert text, and every entity is decoded so no
// `&#x27;`/`<p>` artifact can leak.
const MUTED = "#696F8C";

export function renderNotificationSentence({ actorName, description } = {}) {
  const full = inertHtmlToText(description);
  const name = actorName == null ? "" : String(actorName).trim();

  if (full === "") {
    // No description text: show at least the bold name if we have one (rare).
    return name
      ? [React.createElement("strong", { key: "actor" }, name)]
      : [];
  }

  // Bold the actor name only when the decoded description begins with it
  // (case-insensitive tolerance for BE casing quirks). Slice the displayed name
  // out of `full` so its exact casing/spacing matches the sentence.
  const startsWithName =
    name !== "" &&
    full.slice(0, name.length).toLowerCase() === name.toLowerCase();

  if (!startsWithName) {
    return [
      React.createElement("span", { key: "text", style: { color: MUTED } }, full),
    ];
  }

  const displayedName = full.slice(0, name.length);
  const rest = full.slice(name.length);
  return [
    React.createElement("strong", { key: "actor" }, displayedName),
    React.createElement("span", { key: "rest", style: { color: MUTED } }, rest),
  ];
}

export default renderSafeDescription;
